import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { isAuthorizedAdmin } from '@/lib/admin-access';

interface SuperAdminGuardProps {
  children: React.ReactNode;
}

export default function SuperAdminGuard({ children }: SuperAdminGuardProps) {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        if (!session?.user) {
          setIsChecking(false);
          navigate('/login');
          return;
        }

        const userEmail = (session.user.email || '').toLowerCase().trim();

        if (isAuthorizedAdmin(userEmail)) {
          setHasAccess(true);
        } else {
          navigate('/dashboard');
        }

        setIsChecking(false);
      } catch (err) {
        console.warn('SuperAdminGuard: Session check failed:', (err as Error)?.message || err);
        await supabase.auth.signOut({ scope: 'local' });
        if (!cancelled) {
          setIsChecking(false);
          navigate('/login');
        }
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: '#ffffff' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-foreground-800 text-sm font-medium">Checking access...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return <>{children}</>;
}